import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import chatAPI, { ChatResponse } from '../api/chat';
import { API_BASE_URL } from '../api/tourism';
import { DesignSystem, getColor, getSpacing, getShadow } from '../styles/designSystem';
import { Button } from './ui';
import { useSession } from '../contexts/SessionContext';

interface Message {
  id: string;
  text: string;
  isBot: boolean;
  timestamp: string;
}

interface ChatBotProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper function to format bot messages with proper bullet points
const formatBotMessage = (text: string): React.ReactNode => {
  // Split by common bullet point patterns: *, •, -, or numbered lists
  // First, normalize the text by replacing * with line breaks + bullets
  let formatted = text
    .replace(/\s*\*\s+/g, '\n• ')  // Replace " * " with newline + bullet
    .replace(/\s*•\s+/g, '\n• ')   // Normalize existing bullets
    .replace(/\s*-\s+(?=[A-Z])/g, '\n• ')  // Replace " - " followed by capital letter
    .trim();
  
  // Split into lines and render
  const lines = formatted.split('\n').filter(line => line.trim());
  
  if (lines.length <= 1) {
    return text; // No formatting needed
  }
  
  return (
    <>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {line}
          {index < lines.length - 1 && <br />}
        </React.Fragment>
      ))}
    </>
  );
};

const ChatBot: React.FC<ChatBotProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { sessionId, createNewSession, isCreatingSession } = useSession();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (isOpen && !hasInitialized.current) {
      initializeChat();
    }
  }, [isOpen, sessionId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = () => {
    if (sessionId) {
      const storageKey = `chat_history_${sessionId}`;
      const storedMessages = sessionStorage.getItem(storageKey);
      if (storedMessages) {
        try {
          const parsedMessages = JSON.parse(storedMessages);
          console.log('📜 Loaded chat history for session:', sessionId);
          setMessages(parsedMessages);
          return parsedMessages.length > 0;
        } catch (error) {
          console.error('Error loading chat history:', error);
        }
      }
    }
    return false;
  };

  const saveChatHistory = (messagesToSave: Message[]) => {
    if (sessionId) {
      const storageKey = `chat_history_${sessionId}`;
      sessionStorage.setItem(storageKey, JSON.stringify(messagesToSave));
      console.log('💾 Saved chat history for session:', sessionId);
    }
  };

  const initializeChat = async () => {
    if (hasInitialized.current || isCreatingSession) return;
    hasInitialized.current = true;
    
    try {
      // Create a new session if one doesn't exist
      if (!sessionId) {
        await createNewSession();
        return; // Wait for sessionId to be set, then re-initialize
      }
      
      // Try to load existing chat history
      const hasHistory = loadChatHistory();
      
      if (!hasHistory) {
        // Add welcome message only if no history exists
        const welcomeMessage: Message = {
          id: `welcome_${Date.now()}`,
          text: "Hello! I'm your Rovers Guide from Ladakh Himalayan Rovers. I can help you with travel planning, itineraries, pricing, and everything about your Himalayan adventure! How can I assist you today?",
          isBot: true,
          timestamp: new Date().toISOString()
        };
        const initialMessages = [welcomeMessage];
        setMessages(initialMessages);
        saveChatHistory(initialMessages);
      }
    } catch (error) {
      console.error('Error initializing chat session:', error);
      
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: "Welcome! I'm having a slight connection issue, but I'm here to help. Please try sending a message.",
        isBot: true,
        timestamp: new Date().toISOString()
      };
      const errorMessages = [errorMessage];
      setMessages(errorMessages);
      saveChatHistory(errorMessages);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    // Always use sessionId (not sessionToken)
    if (!sessionId) {
      console.error('No session ID available');
      return;
    }
    
    const userMessage: Message = {
      id: `user_${Date.now()}`,
      text: inputMessage.trim(),
      isBot: false,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => {
      const updated = [...prev, userMessage];
      saveChatHistory(updated);
      return updated;
    });
    setInputMessage('');
    setIsLoading(true);

    // Create a placeholder for the bot message that will be updated with chunks
    const botMessageId = `bot_${Date.now()}`;
    const botMessage: Message = {
      id: botMessageId,
      text: '',
      isBot: true,
      timestamp: new Date().toISOString()
    };
    
    // Add empty bot message to show it's responding
    setMessages(prev => {
      const updated = [...prev, botMessage];
      saveChatHistory(updated);
      return updated;
    });

    try {
      console.log('📤 Sending message with session ID:', sessionId);
      
      await chatAPI.streamMessage(
        userMessage.text,
        sessionId,
        // onChunk callback - receives complete structured response
        (response: any) => {
          setMessages(prev => {
            const updated = [...prev];
            const lastMessage = updated[updated.length - 1];
            
            // Update the last bot message with response text
            if (lastMessage && lastMessage.id === botMessageId) {
              lastMessage.text = response.response_text || response;
            }
            
            saveChatHistory(updated);
            return updated;
          });
        },
        // onComplete callback
        () => {
          console.log('✅ Response received and displayed');
          setIsLoading(false);
        },
        // onError callback
        (error: string) => {
          // Handle session expiry — silently create new session
          if (error === 'SESSION_EXPIRED') {
            console.log('⏰ Session expired, creating new session...');
            createNewSession();
            setMessages([]);
            setIsLoading(false);
            return;
          }
          
          const errorMessage: Message = {
            id: `error_${Date.now()}`,
            text: error || "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to contact us directly for immediate assistance.",
            isBot: true,
            timestamp: new Date().toISOString()
          };
          setMessages(prev => {
            const updated = [...prev];
            // Replace the empty bot message with error message
            if (updated[updated.length - 1]?.id === botMessageId) {
              updated[updated.length - 1] = errorMessage;
            } else {
              updated.push(errorMessage);
            }
            saveChatHistory(updated);
            return updated;
          });
          console.error('Error response:', error);
          setIsLoading(false);
        }
      );
    } catch (error) {
      const errorMessage: Message = {
        id: `error_${Date.now()}`,
        text: "I apologize, but I'm having trouble connecting right now. Please try again in a moment, or feel free to contact us directly for immediate assistance.",
        isBot: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => {
        const updated = [...prev];
        // Replace the empty bot message with error message
        if (updated[updated.length - 1]?.id === botMessageId) {
          updated[updated.length - 1] = errorMessage;
        } else {
          updated.push(errorMessage);
        }
        saveChatHistory(updated);
        return updated;
      });
      console.error('Error sending message:', error);
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickReplies = [
    "Tell me about Ladakh packages",
    "What's included in the tour?",
    "Best time to visit Ladakh"
  ];

  if (!isOpen) return null;

  return (
    <ChatContainer>
      <ChatHeader>
        <HeaderContent>
          <BotAvatar>
            <img src={`${API_BASE_URL}/api/images/app_assets/logo.webp`} alt="Rovers Guide Logo" />
          </BotAvatar>
          <HeaderInfo>
            <BotName>
              Rovers Guide
              <OnlineDot />
            </BotName>
          </HeaderInfo>
        </HeaderContent>
        <CloseButton onClick={onClose}>
          ✕
        </CloseButton>
      </ChatHeader>

      <MessagesContainer>
        {messages.map((message) => (
          <MessageWrapper key={message.id} isBot={message.isBot}>
            <Message isBot={message.isBot}>
              {message.isBot && (
                <MessageAvatar>
                  <img src={`${API_BASE_URL}/api/images/app_assets/logo.webp`} alt="Rovers Guide Logo" />
                </MessageAvatar>
              )}
              <MessageContent isBot={message.isBot}>
                {message.isBot ? formatBotMessage(message.text) : message.text}
                <MessageTime>
                  {new Date(message.timestamp).toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </MessageTime>
              </MessageContent>
            </Message>
          </MessageWrapper>
        ))}

        {isLoading && (
          <MessageWrapper isBot={true}>
            <Message isBot={true}>
              <MessageAvatar></MessageAvatar>
              <TypingIndicator>
                <TypingDot delay={0} />
                <TypingDot delay={0.2} />
                <TypingDot delay={0.4} />
              </TypingIndicator>
            </Message>
          </MessageWrapper>
        )}

        {messages.length <= 1 && !isLoading && (
          <QuickRepliesContainer>
            <QuickRepliesTitle>Quick questions:</QuickRepliesTitle>
            <QuickRepliesGrid>
              {quickReplies.map((reply, index) => (
                <QuickReplyButton
                  key={index}
                  onClick={() => {
                    setInputMessage(reply);
                    // Auto-send after a brief delay for better UX
                    setTimeout(() => sendMessage(), 100);
                  }}
                >
                  {reply}
                </QuickReplyButton>
              ))}
            </QuickRepliesGrid>
          </QuickRepliesContainer>
        )}
        
        <div ref={messagesEndRef} />
      </MessagesContainer>

      <InputContainer>
        <MessageInput
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me anything about your Ladakh adventure..."
          disabled={isLoading}
        />
        <SendButton
          onClick={sendMessage}
          disabled={!inputMessage.trim() || isLoading}
        >
          <SendIcon>→</SendIcon>
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
};

// Sophisticated chat container with glass morphism
const slideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(100%) scale(0.8);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const ChatContainer = styled.div`
  position: fixed;
  bottom: ${getSpacing('6xl')};
  right: ${getSpacing('xl')};
  width: 400px;
  height: 600px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: ${DesignSystem.borderRadius['3xl']};
  border: 1px solid rgba(202, 185, 110, 0.2);
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(202, 185, 110, 0.08);
  display: flex;
  flex-direction: column;
  z-index: ${DesignSystem.zIndex.modal};
  ${css`
    animation: ${slideUp} ${DesignSystem.motion.duration.slow} ${DesignSystem.motion.easing.bounce};
  `}
  
  @media (max-width: 768px) {
    bottom: ${getSpacing('4xl')};
    right: ${getSpacing('lg')};
    left: ${getSpacing('lg')};
    width: auto;
    height: calc(100vh - 120px);
    max-height: 500px;
  }
  @media (max-width: 480px) {
    left: ${getSpacing('md')};
    right: ${getSpacing('md')};
    bottom: ${getSpacing('2xl')};
  }
`;

// Premium header with frosted glass effect
const ChatHeader = styled.div`
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  color: ${getColor('primary.900')};
  padding: ${getSpacing('lg')};
  border-radius: ${DesignSystem.borderRadius['3xl']} ${DesignSystem.borderRadius['3xl']} 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  border-bottom: 1px solid rgba(202, 185, 110, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, transparent, #CAB96E, transparent);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSpacing('md')};
`;

const BotAvatar = styled.div`
  width: 48px;
  height: 48px;
  background: transparent;
  border-radius: ${DesignSystem.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  border: 2px solid rgba(202, 185, 110, 0.4);
  padding: 2px;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: ${DesignSystem.borderRadius.full};
    object-fit: cover;
  }
  @media (max-width: 768px) {
    width: 36px;
    height: 36px;
    font-size: 1.2rem;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const BotName = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${getColor('primary.900')};
`;

const OnlineDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4CAF50;
  display: inline-block;
  box-shadow: 0 0 6px rgba(76, 175, 80, 0.6);
`;

const SessionInfo = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 2px;
  font-family: monospace;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 250px;
  cursor: help;
  
  &:hover {
    opacity: 1;
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${getColor('primary.700')};
  font-size: 1.25rem;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: ${DesignSystem.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
    transform: scale(1.1);
  }
`;

// Messages area with smooth scrolling
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${getSpacing('lg')};
  display: flex;
  flex-direction: column;
  gap: ${getSpacing('lg')};
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${getColor('primary.200')};
    border-radius: ${DesignSystem.borderRadius.full};
  }
  @media (max-width: 768px) {
    padding: ${getSpacing('md')};
  }
`;

const MessageWrapper = styled.div<{ isBot: boolean }>`
  display: flex;
  justify-content: ${props => props.isBot ? 'flex-start' : 'flex-end'};
`;

const Message = styled.div<{ isBot: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${getSpacing('sm')};
  max-width: 80%;
  
  ${props => !props.isBot && `
    flex-direction: row-reverse;
  `}
`;

const MessageAvatar = styled.div`
  width: 32px;
  height: 32px;
  background: transparent;
  border-radius: ${DesignSystem.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 4px;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: ${DesignSystem.borderRadius.full};
    object-fit: cover;
  }
`;

const MessageContent = styled.div<{ isBot: boolean }>`
  background: ${props => props.isBot 
    ? getColor('primary.50')
    : `linear-gradient(135deg, ${getColor('primary.800')}, ${getColor('primary.900')})`
  };
  color: ${props => props.isBot ? getColor('primary.900') : 'white'};
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border-radius: ${props => props.isBot 
    ? `4px ${DesignSystem.borderRadius.xl} ${DesignSystem.borderRadius.xl} ${DesignSystem.borderRadius.xl}`
    : `${DesignSystem.borderRadius.xl} 4px ${DesignSystem.borderRadius.xl} ${DesignSystem.borderRadius.xl}`
  };
  font-size: 0.95rem;
  line-height: 1.5;
  position: relative;
  border: 1px solid ${props => props.isBot ? getColor('primary.100') : 'transparent'};
  ${props => props.isBot && `border-left: 3px solid #CAB96E;`}
`;

const MessageTime = styled.div`
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: ${getSpacing('xs')};
`;

// Elegant typing indicator
const typingAnimation = keyframes`
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const TypingIndicator = styled.div`
  background: ${getColor('primary.50')};
  border: 1px solid ${getColor('primary.100')};
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  border-radius: 4px ${DesignSystem.borderRadius.xl} ${DesignSystem.borderRadius.xl} ${DesignSystem.borderRadius.xl};
  display: flex;
  gap: 4px;
  align-items: center;
`;

const TypingDot = styled.div<{ delay: number }>`
  width: 8px;
  height: 8px;
  background: ${getColor('primary.400')};
  border-radius: ${DesignSystem.borderRadius.full};
  ${props => css`
    animation: ${typingAnimation} 1.4s infinite;
    animation-delay: ${props.delay}s;
  `}
`;

// Quick replies for better UX
const QuickRepliesContainer = styled.div`
  margin-top: ${getSpacing('lg')};
`;

const QuickRepliesTitle = styled.div`
  font-size: 0.875rem;
  color: ${getColor('primary.600')};
  margin-bottom: ${getSpacing('md')};
  font-weight: 500;
`;

const QuickRepliesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${getSpacing('sm')};
`;

const QuickReplyButton = styled.button`
  background: transparent;
  border: 1px solid rgba(202, 185, 110, 0.4);
  color: ${getColor('primary.700')};
  padding: ${getSpacing('xs')} ${getSpacing('lg')};
  border-radius: 50px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  white-space: nowrap;
  
  &:hover {
    background: rgba(202, 185, 110, 0.12);
    border-color: #CAB96E;
    transform: translateY(-1px);
  }
`;

// Premium input area
const InputContainer = styled.div`
  padding: ${getSpacing('lg')};
  border-top: 1px solid ${getColor('primary.100')};
  display: flex;
  gap: ${getSpacing('md')};
  background: transparent;
`;

const MessageInput = styled.input`
  flex: 1;
  border: 1px solid ${getColor('primary.200')};
  border-radius: ${DesignSystem.borderRadius.xl};
  padding: ${getSpacing('md')} ${getSpacing('lg')};
  font-size: 0.95rem;
  outline: none;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  background: white;
  color: #000000;
  
  &:focus {
    border-color: ${getColor('primary.400')};
  }
  
  &::placeholder {
    color: ${getColor('primary.400')};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SendButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled 
    ? getColor('primary.200')
    : '#CAB96E'
  };
  color: white;
  border: none;
  border-radius: ${DesignSystem.borderRadius.full};
  width: 48px;
  height: 48px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${DesignSystem.motion.duration.fast} ${DesignSystem.motion.easing.ease};
  
  &:hover:not(:disabled) {
    transform: scale(1.08);
    box-shadow: 0 4px 16px rgba(202, 185, 110, 0.4);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
  }
`;

const SendIcon = styled.span`
  font-size: 1.25rem;
  font-weight: bold;
`;

export default ChatBot;